import { useMemo, useState } from 'react';

import type { ChartOfAccount, CreateArAdvanceInput } from '@dcc-erp/shared';
import { CreateArAdvanceSchema, dateToInputValue, fmt } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useCoa, useCreateArAdvance, useCustomers } from '../../lib/queries';
import { AppModal } from '../AppModal';
import { SearchSelect } from '../SearchSelect';
import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';

// =============================================================================
// Record a customer advance / deposit (เงินรับล่วงหน้า, AR-12) — customer-first.
//
// CreateAdvanceModal (container) owns customer selection + data fetching; the
// inner CreateAdvanceForm (presentational, router/data-free) owns the bank/cash
// account picker + amount + date + note so the submit payload is unit-testable
// without a query client — the same split RecordReceiptModal / CreatePdcModal use.
// Submit builds { customerId, advanceDate, amount, cashAccountCode, note? } and
// the container validates it via CreateArAdvanceSchema before the POST (the
// service posts the receive JV Dr cash / Cr CUSTOMER_ADVANCE for `amount`).
// =============================================================================

type CashAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

export interface CreateAdvanceFormProps {
  cashAccounts: CashAccount[];
  onSubmit: (payload: CreateArAdvanceInput) => void;
  submitting: boolean;
  // Container-supplied; the customer is already chosen upstream. Defaults to ''
  // so the presentational form can be rendered in isolation (tests assert only
  // the submitted payload shape).
  customerId?: string;
}

export function CreateAdvanceForm({
  cashAccounts,
  onSubmit,
  submitting,
  customerId = '',
}: CreateAdvanceFormProps) {
  const today = new Date();
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  const [advanceDate, setAdvanceDate] = useState(dateToInputValue(today));
  const [note, setNote] = useState('');

  const cashOptions = cashAccounts.map((a) => ({
    value: a.code,
    label: `${a.code} · ${a.nameTh}`,
  }));
  const cashLabel = (code: string) => {
    const hit = cashAccounts.find((a) => a.code === code);
    return hit ? `${hit.code} · ${hit.nameTh}` : code;
  };

  const disabled = !cashAccountCode || !advanceDate || amount <= 0 || submitting;

  function handleSubmit() {
    const payload: CreateArAdvanceInput = {
      customerId,
      advanceDate,
      amount,
      cashAccountCode,
      ...(note ? { note } : {}),
    };
    onSubmit(payload);
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>บัญชีเงินสด / ธนาคาร (รับเงินเข้าบัญชี)</label>
          <SearchSelect
            value={cashAccountCode}
            onChange={setCashAccountCode}
            options={cashOptions}
            getLabel={cashLabel}
            placeholder="— เลือกบัญชีธนาคาร —"
          />
        </div>
        <div>
          <label style={modalLabel}>จำนวนเงิน</label>
          <input
            type="number"
            className="input tnum"
            aria-label="amount"
            value={amount || ''}
            min={0}
            step={0.01}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
          />
        </div>
      </div>

      <div>
        <label style={modalLabel}>วันที่รับเงิน</label>
        <input
          type="date"
          className="input"
          aria-label="advance-date"
          value={advanceDate}
          onChange={(e) => setAdvanceDate(e.target.value)}
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <input
          className="input"
          aria-label="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="หมายเหตุ"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--brand-tint)',
          fontSize: 14,
        }}
      >
        <span className="muted">บันทึก Dr เงินสด/ธนาคาร · Cr รายได้รับล่วงหน้า</span>
        <b className="tnum" style={{ fontSize: 16 }}>
          {fmt(amount, 2)} บาท
        </b>
      </div>

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึกเงินรับล่วงหน้า'}
        </button>
      </div>
    </div>
  );
}

export interface CreateAdvanceModalProps {
  companyId: string;
  // `| undefined` (not just optional) so callers may pass an explicit undefined
  // under exactOptionalPropertyTypes.
  presetCustomerId?: string | undefined;
  onClose: () => void;
}

export function CreateAdvanceModal({
  companyId,
  presetCustomerId,
  onClose,
}: CreateAdvanceModalProps) {
  const [customerId, setCustomerId] = useState<string>(presetCustomerId ?? '');

  const { data: customers = [] } = useCustomers(companyId, true);
  const create = useCreateArAdvance(companyId);

  // Cash + bank accounts — codes in the 1110-1119 range (cash equivalents),
  // exactly as RecordReceiptModal / ClearPdcModal pick them.
  const { data: coa = [] } = useCoa(companyId, true);
  const cashAccounts = useMemo(
    () =>
      coa.filter((c) => c.type === 'ASSET' && c.isActive && c.code >= '1110' && c.code < '1120'),
    [coa],
  );

  const customerLabel = useMemo(() => {
    const m = new Map(customers.map((c) => [c.id, `${c.code} · ${c.nameTh}`]));
    return (id: string) => m.get(id) ?? id;
  }, [customers]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: `${c.code} · ${c.nameTh}` })),
    [customers],
  );

  async function handleSubmit(payload: CreateArAdvanceInput) {
    // Validate the wire shape (customerId/amount/date/cashAccountCode) before the
    // POST. On failure the mutateAsync rejects, the modal stays open + create.error
    // renders below so the user can retry.
    const data = CreateArAdvanceSchema.parse({ ...payload, customerId });
    await create.mutateAsync(data);
    onClose();
  }

  // WS8 R2 — standard AppModal (1200px) replaces the bespoke 600px chrome.
  // Container-only: CreateAdvanceForm keeps its body + in-body submit button.
  return (
    <AppModal title="บันทึกเงินรับล่วงหน้า" onClose={onClose}>
      <div style={{ padding: '0 22px 0' }}>
        <label style={modalLabel}>ลูกค้า</label>
        <SearchSelect
          value={customerId}
          onChange={setCustomerId}
          options={customerOptions}
          getLabel={customerLabel}
          placeholder="— เลือกลูกค้า —"
          disabled={!!presetCustomerId}
        />
      </div>

      {customerId ? (
        <CreateAdvanceForm
          key={customerId}
          cashAccounts={cashAccounts}
          onSubmit={(payload) => void handleSubmit(payload)}
          submitting={create.isPending}
          customerId={customerId}
        />
      ) : (
        <div style={{ ...modalBody, color: 'var(--text-muted)' }}>
          เลือกลูกค้าเพื่อบันทึกเงินรับล่วงหน้า
        </div>
      )}

      {create.error && (
        <div style={{ ...modalError, padding: '0 22px 16px' }}>
          บันทึกไม่สำเร็จ: {create.error.message}
        </div>
      )}
    </AppModal>
  );
}
